import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import { apiErrorMessage } from './prescriptionForm';
import {
  ASSIGNMENT_MODE,
  describeMode,
  validateAthleteForm,
  buildAthleteRequest,
  INVITATION_SENT,
  validateAssignment,
  buildAssignmentRequest,
  validateGroupForm,
  buildGroupRequest,
  athletesNotIn,
  describeAssignment,
  isActive,
} from './coachingForms';
import {
  BULK_MODE,
  describeBulkMode,
  isSelected,
  toggleAthlete,
  selectAll,
  isAllSelected,
  pruneSelection,
  selectionSummary,
  validateBulkAssignment,
  buildBulkAssignmentRequest,
} from './athleteSelection';
import {
  canReplace,
  versionLabel,
  emptyReplacement,
  validateReplacement,
  buildSupersedeRequest,
  supersedePath,
  replacementNotice,
} from './assignmentVersions';
import '../styles/Coaching.css';

// Coaching: who you train, and what you have given them.
//
// The screen's job is to make the copy-versus-live distinction impossible to
// miss. It is invisible until it bites — editing a copy affects one athlete,
// editing a shared calendar affects the squad — so the consequence is written
// out beside the choice rather than left to a mode name.
//
// # WHERE THE DAILY VIEW WENT
//
// The roster roll-up used to open this page: who trained, who is overdue, who
// checked in low. It is now <RosterOverview /> on /home, because it answers the
// question a coach opens the app with, and here it sat behind a nav click and
// above three forms — add an athlete, create a group, give one out — which are
// occasional, deliberate administration.
//
// This page is that administration. It deliberately does NOT render the roll-up
// as well: two copies of a list drift, and the one you are not looking at is
// always the stale one.

const Coaching = () => {
  const { token } = useAuth();

  const [athletes, setAthletes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [athleteForm, setAthleteForm] = useState({ email: '', displayName: '' });
  const [athleteErrors, setAthleteErrors] = useState({});
  const [groupForm, setGroupForm] = useState({ name: '', calendarProgramId: '' });
  const [groupErrors, setGroupErrors] = useState({});
  const [assignForm, setAssignForm] = useState({
    programId: '', mode: ASSIGNMENT_MODE.COPY, athleteId: '', groupId: '', startDate: '',
  });
  const [assignErrors, setAssignErrors] = useState({});
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  // One replacement form open at a time: the row it belongs to is in it.
  const [replacement, setReplacement] = useState(null);
  const [replaceErrors, setReplaceErrors] = useState({});

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    try {
      const [rosterRes, groupsRes, assignRes, programsRes] = await Promise.all([
        axios.get(buildApiUrl('/athletes'), authConfig()),
        axios.get(buildApiUrl('/groups'), authConfig()),
        axios.get(buildApiUrl('/assignments'), authConfig()),
        axios.get(buildApiUrl('/programs'), authConfig()),
      ]);
      const roster = rosterRes.data?.athletes ?? [];
      setAthletes(roster);
      // Somebody removed while ticked must not stay in the list unseen — see
      // pruneSelection.
      setSelectedAthletes((selected) => pruneSelection(selected, roster));
      setGroups(groupsRes.data?.groups ?? []);
      setAssignments(assignRes.data?.assignments ?? []);
      setPrograms(programsRes.data ?? []);
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'Could not load your coaching.') });
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (work, success) => {
    setBusy(true);
    setNotice(null);
    try {
      await work();
      await load();
      setNotice({ tone: 'success', message: success });
      return true;
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'That did not work.') });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addAthlete = async (event) => {
    event.preventDefault();
    const errors = validateAthleteForm(athleteForm);
    setAthleteErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const ok = await run(
      () => axios.post(buildApiUrl('/athletes'), buildAthleteRequest(athleteForm), authConfig()),
      INVITATION_SENT
    );
    if (ok) setAthleteForm({ email: '', displayName: '' });
  };

  const createGroup = async (event) => {
    event.preventDefault();
    const errors = validateGroupForm(groupForm);
    setGroupErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const ok = await run(
      () => axios.post(buildApiUrl('/groups'), buildGroupRequest(groupForm), authConfig()),
      'Group created.'
    );
    if (ok) setGroupForm({ name: '', calendarProgramId: '' });
  };

  const assignSeveral = async () => {
    const errors = validateBulkAssignment(assignForm, selectedAthletes);
    setAssignErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const count = selectedAthletes.length;
    const ok = await run(
      () => axios.post(buildApiUrl('/assignments/bulk'),
        buildBulkAssignmentRequest(assignForm, selectedAthletes, athletes), authConfig()),
      `A copy each created for ${count} athlete${count === 1 ? '' : 's'}.`
    );
    if (ok) {
      setSelectedAthletes([]);
      setAssignForm({ ...assignForm, startDate: '' });
    }
  };

  const assign = async (event) => {
    event.preventDefault();
    if (assignForm.mode === BULK_MODE) {
      await assignSeveral();
      return;
    }
    const errors = validateAssignment(assignForm);
    setAssignErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const mode = describeMode(assignForm.mode);
    const ok = await run(
      () => axios.post(buildApiUrl('/assignments'),
        buildAssignmentRequest(assignForm), authConfig()),
      `${mode.title} created.`
    );
    if (ok) {
      setAssignForm({ ...assignForm, athleteId: '', groupId: '', startDate: '' });
    }
  };

  const openReplacement = (assignment) => {
    setReplaceErrors({});
    setReplacement(emptyReplacement(assignment));
  };

  const replace = async (event, assignment) => {
    event.preventDefault();
    const errors = validateReplacement(replacement);
    setReplaceErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const ok = await run(
      () => axios.post(buildApiUrl(supersedePath(assignment)),
        buildSupersedeRequest(replacement), authConfig()),
      replacementNotice(assignment, replacement, programs)
    );
    if (ok) setReplacement(null);
  };

  const mode = assignForm.mode === BULK_MODE
    ? describeBulkMode()
    : describeMode(assignForm.mode);

  if (loading) {
    return <BaseLayout><p className="coaching-status">Loading your coaching…</p></BaseLayout>;
  }

  return (
    <BaseLayout>
      <div className="coaching">
        <header className="coaching-header">
          <span className="pill">Coaching</span>
          <h1 className="page-title">Athletes and squads</h1>
          <p className="meta-copy">
            Give a programme to one athlete as their own copy, or to a group as a
            shared calendar.
          </p>
        </header>

        {notice && (
          <p
            className={notice.tone === 'error' ? 'status-error' : 'status-success'}
            role="status"
            aria-live="polite"
          >
            {notice.message}
          </p>
        )}

        <section className="card coaching-panel">
          <h2 className="section-title">Roster</h2>
          {/* The roster is consent-based: an email typed here is an invitation,
              and the athlete appears below only once they accept it from their
              own home. Said up front, because a coach who expected them to
              appear straight away would otherwise read the empty list as a
              failure and keep re-typing the address. */}
          <p className="meta-copy">
            You can only assign to somebody on your roster. They need an account
            already. Inviting them does not email anybody — they see the
            invitation on their home screen and appear here once they accept.
          </p>

          {athletes.length === 0 ? (
            <p className="meta-copy empty-state-text">Nobody yet.</p>
          ) : (
            <ul className="coaching-list">
              {athletes.map((athlete) => (
                <li key={athlete.athleteId}>
                  <span>
                    <strong>{athlete.displayName || athlete.username}</strong>
                    {athlete.displayName && (
                      <span className="muted"> · {athlete.username}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={busy}
                    onClick={() => run(
                      () => axios.delete(buildApiUrl(`/athletes/${athlete.athleteId}`), authConfig()),
                      'Removed from your roster.'
                    )}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form className="coaching-form" onSubmit={addAthlete}>
            <div className="coaching-field">
              <label htmlFor="athlete-email">Their email</label>
              <input
                id="athlete-email"
                type="email"
                value={athleteForm.email}
                onChange={(e) => setAthleteForm({ ...athleteForm, email: e.target.value })}
                aria-invalid={Boolean(athleteErrors.email)}
              />
              {athleteErrors.email && <span className="field-error">{athleteErrors.email}</span>}
            </div>
            <div className="coaching-field">
              <label htmlFor="athlete-name">What you call them</label>
              <input
                id="athlete-name"
                value={athleteForm.displayName}
                onChange={(e) => setAthleteForm({ ...athleteForm, displayName: e.target.value })}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={busy}>Invite athlete</button>
          </form>
        </section>

        <section className="card coaching-panel">
          <h2 className="section-title">Groups</h2>
          <p className="meta-copy">
            A group follows one calendar live. Change it and every member sees the
            change; what they log stays their own.
          </p>

          {groups.length === 0 ? (
            <p className="meta-copy empty-state-text">No groups yet.</p>
          ) : (
            <ul className="coaching-list coaching-groups">
              {groups.map((group) => (
                <li key={group.id}>
                  <div>
                    <strong>{group.name}</strong>
                    <span className="muted">
                      {group.calendarName
                        ? ` · following ${group.calendarName}`
                        : ' · no calendar yet'}
                    </span>
                    <div className="muted coaching-members">
                      {group.memberCount === 0
                        ? 'No members'
                        : `${group.memberCount} member${group.memberCount === 1 ? '' : 's'}: ${group.members.join(', ')}`}
                    </div>
                  </div>
                  <label className="coaching-inline">
                    <span className="sr-only">Add a member to {group.name}</span>
                    <select
                      value=""
                      disabled={busy}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        run(
                          () => axios.post(buildApiUrl(`/groups/${group.id}/members`),
                            { athleteId: Number(e.target.value) }, authConfig()),
                          'Member added.'
                        );
                      }}
                    >
                      <option value="">Add a member…</option>
                      {athletesNotIn(athletes, group).map((athlete) => (
                        <option key={athlete.athleteId} value={athlete.athleteId}>
                          {athlete.displayName || athlete.username}
                        </option>
                      ))}
                    </select>
                  </label>
                </li>
              ))}
            </ul>
          )}

          <form className="coaching-form" onSubmit={createGroup}>
            <div className="coaching-field">
              <label htmlFor="group-name">Group name</label>
              <input
                id="group-name"
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                aria-invalid={Boolean(groupErrors.name)}
              />
              {groupErrors.name && <span className="field-error">{groupErrors.name}</span>}
            </div>
            <div className="coaching-field">
              <label htmlFor="group-calendar">Calendar</label>
              <select
                id="group-calendar"
                value={groupForm.calendarProgramId}
                onChange={(e) => setGroupForm({ ...groupForm, calendarProgramId: e.target.value })}
              >
                <option value="">None yet</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>{program.name}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={busy}>Create group</button>
          </form>
        </section>

        <section className="card coaching-panel">
          <h2 className="section-title">Give out a programme</h2>

          <form className="coaching-form coaching-assign" onSubmit={assign}>
            <div className="coaching-field">
              <label htmlFor="assign-program">Programme</label>
              <select
                id="assign-program"
                value={assignForm.programId}
                onChange={(e) => setAssignForm({ ...assignForm, programId: e.target.value })}
                aria-invalid={Boolean(assignErrors.programId)}
              >
                <option value="">Choose…</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>{program.name}</option>
                ))}
              </select>
              {assignErrors.programId && (
                <span className="field-error">{assignErrors.programId}</span>
              )}
            </div>

            <div className="coaching-field">
              <label htmlFor="assign-mode">How</label>
              <select
                id="assign-mode"
                value={assignForm.mode}
                onChange={(e) => setAssignForm({ ...assignForm, mode: e.target.value })}
              >
                <option value={ASSIGNMENT_MODE.COPY}>One athlete — their own copy</option>
                <option value={BULK_MODE}>Several athletes — a copy each</option>
                <option value={ASSIGNMENT_MODE.LIVE}>A group — shared calendar</option>
              </select>
            </div>

            {/* The consequence, written out beside the choice. This is the thing
                the screen exists to communicate: the difference is invisible
                until it bites. */}
            <p className="coaching-mode-note">
              <strong>{mode.title}.</strong> {mode.detail}
            </p>

            {assignForm.mode === BULK_MODE && (
              <fieldset
                className="coaching-field coaching-athlete-picker"
                aria-invalid={Boolean(assignErrors.athleteIds)}
              >
                <legend>Athletes</legend>
                {athletes.length === 0 ? (
                  <p className="meta-copy empty-state-text">Nobody on your roster yet.</p>
                ) : (
                  <>
                    <div className="coaching-inline">
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={isAllSelected(selectedAthletes, athletes)}
                        onClick={() => setSelectedAthletes(selectAll(athletes))}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={selectedAthletes.length === 0}
                        onClick={() => setSelectedAthletes([])}
                      >
                        Clear
                      </button>
                    </div>
                    <ul className="coaching-checklist">
                      {athletes.map((athlete) => (
                        <li key={athlete.athleteId}>
                          <label>
                            <input
                              type="checkbox"
                              checked={isSelected(selectedAthletes, athlete.athleteId)}
                              onChange={() => setSelectedAthletes(
                                (selected) => toggleAthlete(selected, athlete.athleteId)
                              )}
                            />
                            {' '}
                            {athlete.displayName || athlete.username}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <span className="muted" aria-live="polite">
                  {selectionSummary(selectedAthletes, athletes)}
                </span>
                {assignErrors.athleteIds && (
                  <span className="field-error">{assignErrors.athleteIds}</span>
                )}
              </fieldset>
            )}

            {assignForm.mode === BULK_MODE ? null : assignForm.mode === ASSIGNMENT_MODE.COPY ? (
              <div className="coaching-field">
                <label htmlFor="assign-athlete">Athlete</label>
                <select
                  id="assign-athlete"
                  value={assignForm.athleteId}
                  onChange={(e) => setAssignForm({ ...assignForm, athleteId: e.target.value })}
                  aria-invalid={Boolean(assignErrors.athleteId)}
                >
                  <option value="">Choose…</option>
                  {athletes.map((athlete) => (
                    <option key={athlete.athleteId} value={athlete.athleteId}>
                      {athlete.displayName || athlete.username}
                    </option>
                  ))}
                </select>
                {assignErrors.athleteId && (
                  <span className="field-error">{assignErrors.athleteId}</span>
                )}
              </div>
            ) : (
              <div className="coaching-field">
                <label htmlFor="assign-group">Group</label>
                <select
                  id="assign-group"
                  value={assignForm.groupId}
                  onChange={(e) => setAssignForm({ ...assignForm, groupId: e.target.value })}
                  aria-invalid={Boolean(assignErrors.groupId)}
                >
                  <option value="">Choose…</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
                {assignErrors.groupId && (
                  <span className="field-error">{assignErrors.groupId}</span>
                )}
              </div>
            )}

            <div className="coaching-field">
              <label htmlFor="assign-start">Start date</label>
              <input
                id="assign-start"
                type="date"
                value={assignForm.startDate}
                onChange={(e) => setAssignForm({ ...assignForm, startDate: e.target.value })}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={busy}>Assign</button>
          </form>

          <h3 className="section-subtitle">Given out</h3>
          {assignments.length === 0 ? (
            <p className="meta-copy empty-state-text">Nothing assigned yet.</p>
          ) : (
            <ul className="coaching-list">
              {assignments.map((assignment) => (
                <li key={assignment.id} className={isActive(assignment) ? '' : 'archived'}>
                  <span>
                    {describeAssignment(assignment)}
                    {versionLabel(assignment) && (
                      <span className="muted"> · {versionLabel(assignment)}</span>
                    )}
                    {!isActive(assignment) && <span className="muted"> · archived</span>}
                    {assignment.startDate && (
                      <span className="muted"> · from {assignment.startDate}</span>
                    )}
                  </span>
                  <span className="coaching-inline">
                    {/* A copy is the athlete's programme, not the coach's, so
                        there is nothing here to open — the coach's original is
                        in Programs. A live calendar IS the coach's. */}
                    {assignment.mode === ASSIGNMENT_MODE.LIVE && (
                      <Link className="btn-ghost" to={`/programs/${assignment.programId}/edit`}>
                        Open calendar
                      </Link>
                    )}
                    {canReplace(assignment) && replacement?.assignmentId !== assignment.id && (
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={busy}
                        onClick={() => openReplacement(assignment)}
                      >
                        Replace with…
                      </button>
                    )}
                    {isActive(assignment) && (
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={busy}
                        onClick={() => run(
                          () => axios.delete(buildApiUrl(`/assignments/${assignment.id}`), authConfig()),
                          'Assignment archived. Their programme and log are untouched.'
                        )}
                      >
                        Archive
                      </button>
                    )}
                  </span>
                  {replacement?.assignmentId === assignment.id && (
                    <form
                      className="coaching-form coaching-replace"
                      aria-label={`Replace ${describeAssignment(assignment)}`}
                      onSubmit={(event) => replace(event, assignment)}
                    >
                      <p className="muted">
                        Publishes a new version. Their current block is archived, not
                        deleted — everything they logged on it stays — and their
                        calendar switches on the start date.
                      </p>
                      <div className="coaching-field">
                        <label htmlFor={`replace-program-${assignment.id}`}>New block</label>
                        <select
                          id={`replace-program-${assignment.id}`}
                          value={replacement.programId}
                          onChange={(e) => setReplacement({ ...replacement, programId: e.target.value })}
                          aria-invalid={Boolean(replaceErrors.programId)}
                        >
                          <option value="">Choose…</option>
                          {programs.map((program) => (
                            <option key={program.id} value={program.id}>{program.name}</option>
                          ))}
                        </select>
                        {replaceErrors.programId && (
                          <span className="field-error">{replaceErrors.programId}</span>
                        )}
                      </div>
                      <div className="coaching-field">
                        <label htmlFor={`replace-start-${assignment.id}`}>New version starts</label>
                        <input
                          id={`replace-start-${assignment.id}`}
                          type="date"
                          value={replacement.startDate}
                          onChange={(e) => setReplacement({ ...replacement, startDate: e.target.value })}
                          aria-invalid={Boolean(replaceErrors.startDate)}
                        />
                        {replaceErrors.startDate && (
                          <span className="field-error">{replaceErrors.startDate}</span>
                        )}
                      </div>
                      <span className="coaching-inline">
                        <button type="submit" className="btn-primary" disabled={busy}>
                          Publish new version
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={busy}
                          onClick={() => setReplacement(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    </form>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </BaseLayout>
  );
};

export default Coaching;
